import React, { useMemo, useRef, useState } from 'react';
import DropDownBox from 'devextreme-react/drop-down-box';
import TreeView from 'devextreme-react/tree-view';
import { XData, XDevExtreme } from '@core/utils';
import { useOnChangeEffect } from '@core/hooks';

type TreeViewSelectVal = string | number | null;

type ItemData = {
  [keyExpr: string]: TreeViewSelectVal;
};

type HandleItemClickFn = (data: {
  itemData: ItemData;
  itemNotNode: boolean;
  allowOnValueCall: boolean;
}) => void;

export type TreeViewSelectProps = {
  dataSource: object;
  keyExpr?: string;
  displayExpr?: string;
  parentIdExpr?: string;
  hasItemsExpr?: string;
  disabled?: boolean;
  selectionMode?: 'single' | 'multiple';
  selectNodesRecursive?: boolean;
  onlyNodesSelect?: boolean;
  valueExpr?: string;
  value: TreeViewSelectVal | TreeViewSelectVal[];
  /**
   * @returns false if onValueChanged has to be canceled
   */
  onValueChange?: (
    value: TreeViewSelectVal | TreeViewSelectVal[]
  ) => boolean | void;
  onValueChanged?: (cbVal: {
    value: TreeViewSelectVal | TreeViewSelectVal[];
  }) => void;
  onItemClick?: (itemData: unknown) => void;
  virtualModeEnabled?: boolean;
  searchExpr?: string;
  searchEnabled?: boolean;
};

interface OnKeyDownParams {
  event?: { keyCode: number };
  component?: { instance(): { blur(): void } };
}

const TreeViewSelect: React.FC<TreeViewSelectProps> = ({
  dataSource,
  keyExpr = 'Id',
  displayExpr = 'Nazwa',
  parentIdExpr = 'ParentId',
  selectionMode = 'single',
  selectNodesRecursive = false,
  hasItemsExpr,
  disabled,
  valueExpr = 'Id',
  value,
  onValueChange,
  onValueChanged,
  onItemClick,
  virtualModeEnabled = true,
  searchEnabled = false,
  searchExpr,
  onlyNodesSelect,
  children
}) => {
  const [searchValue, setSearchValue] = useState('');
  const dropDownBoxRef = useRef(null);
  const treeViewRef = useRef(null);
  const itemToUnselect = useRef<string | number | null>(null);

  useOnChangeEffect(() => {
    const treeView = treeViewRef.current as any;

    if (!treeView) return;

    if (!value || (XData.isArray(value) && value.length === 0)) {
      // reset on val cleanup
      treeView.option('dataSource', []);
    }
  }, [value]);

  const closeDropdown = () => {
    (dropDownBoxRef.current as any).close();
  };

  const toggleItem = (itemKey: string | number) => {
    const treeView = treeViewRef.current as any;
    const selectedKeys = treeView.getSelectedNodeKeys();

    const itemIsSelected = !!selectedKeys.find(
      (x: string | number) => x === itemKey
    );

    if (!itemIsSelected) {
      treeView.expandItem(itemKey);
    } else if (selectionMode === 'single') {
      treeView.collapseItem(itemKey);
    }
  };

  const handleSearchedClick = (itemData: ItemData, itemNotNode: boolean) => {
    setSearchValue('');

    const itemKey = itemData[keyExpr] as string | number;

    if (itemNotNode) {
      toggleItem(itemKey);

      return;
    }

    closeDropdown();
  };

  const fireValHandlers = (val: TreeViewSelectVal | TreeViewSelectVal[]) => {
    const allowOnValChanged = onValueChange
      ? onValueChange(val) !== false
      : true;

    if (onValueChanged && allowOnValChanged) {
      onValueChanged({ value: val });
    }
  };

  const handleSingleModeItemClick: HandleItemClickFn = ({
    itemData,
    itemNotNode,
    allowOnValueCall
  }) => {
    if (!searchValue && !onlyNodesSelect) {
      closeDropdown();
    } else {
      handleSearchedClick(itemData, itemNotNode);
    }

    if (allowOnValueCall) {
      fireValHandlers(itemData[valueExpr]);
    }

    if (onItemClick) {
      onItemClick(itemData);
    }
  };

  const determineMultiModeVal = (id: TreeViewSelectVal) => {
    if (!value || typeof value !== 'object') {
      return [id];
    }

    const idFound = !!value.find((x) => x === id);

    if (!idFound) {
      return [...value, id];
    }

    return value.filter((x) => x !== id);
  };

  const handleMultipleModeItemClick: HandleItemClickFn = ({
    itemData,
    itemNotNode,
    allowOnValueCall
  }) => {
    if (!allowOnValueCall) {
      itemToUnselect.current = itemData[keyExpr];
      handleSearchedClick(itemData, itemNotNode);
      return;
    }

    const newVal = determineMultiModeVal(itemData[valueExpr]);

    fireValHandlers(newVal);

    if (onItemClick) {
      onItemClick(itemData);
    }
  };

  const handleItemClick = (itemData: ItemData) => {
    const itemNotNode = hasItemsExpr ? !!itemData[hasItemsExpr] : false;
    const allowOnValueCall = onlyNodesSelect ? !itemNotNode : true;

    const handlersData = { itemData, itemNotNode, allowOnValueCall };

    if (selectionMode === 'single') {
      handleSingleModeItemClick(handlersData);
    } else {
      handleMultipleModeItemClick(handlersData);
    }
  };

  const onSelectionChanged = () => {
    if (!itemToUnselect.current || selectionMode === 'single') return;

    (treeViewRef.current as any).unselectItem(itemToUnselect.current);

    itemToUnselect.current = null;
  };

  const searchEditorOptions = useMemo(() => {
    const onKeyDown = ({ event, component }: OnKeyDownParams) => {
      if (event?.keyCode === 13) {
        component?.instance().blur();
      }
    };

    return {
      onValueChanged: ({ value }: { value?: string }) =>
        setSearchValue(!value || value.length > 2 ? value || '' : ''),
      valueChangeEvent: 'blur',
      onKeyDown: onKeyDown as any
    };
  }, []);

  const renderDropDownContent = () => (
    <TreeView
      virtualModeEnabled={virtualModeEnabled}
      onInitialized={(e) => XDevExtreme.setInstanceToRef(treeViewRef, e)}
      dataStructure="plain"
      focusStateEnabled={false}
      dataSource={dataSource}
      selectionMode={selectionMode}
      selectNodesRecursive={selectNodesRecursive}
      selectByClick
      keyExpr={keyExpr}
      displayExpr={displayExpr}
      parentIdExpr={parentIdExpr}
      searchEnabled={searchEnabled}
      searchExpr={searchExpr}
      searchValue={searchValue}
      searchEditorOptions={searchEditorOptions}
      onItemClick={({ itemData }) => handleItemClick(itemData)}
      onSelectionChanged={onSelectionChanged}
      hasItemsExpr={hasItemsExpr}
    />
  );

  return (
    <DropDownBox
      onInitialized={(e) => XDevExtreme.setInstanceToRef(dropDownBoxRef, e)}
      contentRender={renderDropDownContent}
      disabled={disabled}
      dataSource={dataSource}
      value={value}
      displayExpr={displayExpr}
      valueExpr={valueExpr}
    >
      {children}
    </DropDownBox>
  );
};

export default TreeViewSelect;
